import { Suspense, useEffect, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { EffectComposer, Bloom, Vignette } from '@react-three/postprocessing';
import { KernelSize } from 'postprocessing';
import * as THREE from 'three';
import Earth from './Earth';
import Atmosphere from './Atmosphere';
import StarField from './StarField';
import LandPin from './LandPin';
import SatelliteTiles from './SatelliteTiles';
import GlobeOverlay from './GlobeOverlay';
import useDeviceQuality from './useDeviceQuality';
import { GLOBE_EULER, worldDir, worldToLatLng, zoomToDistance } from './latLonToVec3';

const ITALY = [42.5, 12.5];
const MIN_DIST = 1.03;
const MAX_DIST = 6;
const LIGHT_OFFSET = new THREE.Vector3(1.6, 1.3, 0);

export const pinLatLng = (l) => ({
  lat: Number(l?.location?.lat ?? l?.lat),
  lng: Number(l?.location?.lng ?? l?.lng),
});

// Gestisce voli camera, luce che segue lo sguardo e dissolvenza nuvole in avvicinamento.
function CameraRig({ focus, dist, apiRef, cloudsRef, lightRef }) {
  const { camera, controls } = useThree();
  const anim = useRef(null);

  const flyTo = (dir, d, dur = 1700) => {
    anim.current = {
      fromDir: camera.position.clone().normalize(),
      fromDist: camera.position.length(),
      dir: dir.clone().normalize(),
      dist: Math.min(MAX_DIST, Math.max(MIN_DIST, d)),
      t0: performance.now(),
      dur,
    };
  };

  useEffect(() => {
    camera.position.copy(worldDir(focus[0], focus[1]).multiplyScalar(7.5));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    flyTo(worldDir(focus[0], focus[1]), dist);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus[0], focus[1], dist]);

  useEffect(() => {
    apiRef.current = {
      flyTo: (lat, lng, d) => flyTo(worldDir(lat, lng), d ?? camera.position.length()),
      flyCloser: (lat, lng, d) => flyTo(worldDir(lat, lng), Math.min(d, camera.position.length())),
      zoomBy: (f) => flyTo(camera.position, 1 + (camera.position.length() - 1) * f, 650),
      reset: () => flyTo(worldDir(focus[0], focus[1]), dist),
    };
  });

  useFrame(() => {
    const a = anim.current;
    if (a) {
      const t = Math.min(1, (performance.now() - a.t0) / a.dur);
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const d = a.fromDist + (a.dist - a.fromDist) * e;
      camera.position.copy(a.fromDir.clone().lerp(a.dir, e).normalize().multiplyScalar(d));
      camera.lookAt(0, 0, 0);
      if (t >= 1) anim.current = null;
    }
    const h = camera.position.length() - 1;
    if (controls) {
      controls.rotateSpeed = 0.55 * Math.min(1, Math.max(0.03, h / 2.3));
      controls.zoomSpeed = 0.6 * Math.min(1, Math.max(0.08, h / 1.5));
    }
    if (cloudsRef.current) cloudsRef.current.material.opacity = 0.32 * Math.min(1, Math.max(0, (h - 0.5) / 0.9));
    if (lightRef.current) {
      lightRef.current.position.copy(LIGHT_OFFSET).applyQuaternion(camera.quaternion).add(camera.position.clone().multiplyScalar(2));
    }
  });
  return null;
}

function useInView(ref) {
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) return undefined;
    const io = new IntersectionObserver(([en]) => setInView(en.isIntersecting), { rootMargin: '150px' });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return inView;
}

export default function GlobeMap3D({
  listings = [], height = '82vh', focus = ITALY, zoom = 6,
  pickedPoint, onPick, myLocation, onSelect, showHud = true, hudLabel = 'terreni · Italia',
  testId = 'globe-map-3d',
}) {
  const quality = useDeviceQuality();
  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapRef = useRef();
  const apiRef = useRef();
  const cloudsRef = useRef();
  const lightRef = useRef();
  const labelPortal = useRef(null);
  const inView = useInView(wrapRef);
  const dist = zoomToDistance(zoom);
  const f = [Number(focus?.[0]) || ITALY[0], Number(focus?.[1]) || ITALY[1]];

  const handlePin = (l) => {
    const { lat, lng } = pinLatLng(l);
    apiRef.current?.flyCloser(lat, lng, zoomToDistance(9));
    if (onSelect) onSelect(l); else setSelected(l);
  };

  const handleGlobeClick = (e) => {
    if (!onPick || e.delta > 5) return;
    e.stopPropagation();
    onPick(worldToLatLng(e.point));
  };

  const picked = pickedPoint && isFinite(pickedPoint.lat) && isFinite(pickedPoint.lng) ? pickedPoint : null;

  return (
    <div
      ref={wrapRef}
      className={`relative w-full rounded-3xl overflow-hidden ${onPick ? 'globe-pick' : 'hero3d-grab'}`}
      style={{
        height,
        background: 'radial-gradient(ellipse at 50% 55%, #0d1826 0%, #050709 55%, #000 100%)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
      data-testid={testId}
    >
      <Canvas
        dpr={quality.dpr}
        resize={{ offsetSize: true }}
        frameloop={inView ? 'always' : 'never'}
        camera={{ fov: 34, near: 0.004, far: 200, position: [0, 0, 7.5] }}
        gl={{ antialias: !quality.lite, alpha: true, powerPreference: 'high-performance' }}
        onCreated={() => setTimeout(() => setReady(true), 220)}
      >
        <Suspense fallback={null}>
          <CameraRig focus={f} dist={dist} apiRef={apiRef} cloudsRef={cloudsRef} lightRef={lightRef}/>
          <ambientLight intensity={0.22}/>
          <directionalLight ref={lightRef} intensity={2.1} color="#fff4dc"/>
          <directionalLight position={[-3, -1, -2]} intensity={0.25} color="#b4ff00"/>
          <StarField count={quality.starsCount} radius={30}/>

          <Atmosphere radius={1} color="#8dd8ff" intensity={0.7}/>
          <Atmosphere radius={1} color="#b4ff00" intensity={0.22}/>

          <group rotation={GLOBE_EULER}>
            <group onClick={handleGlobeClick}>
              <Earth radius={1} rotate={false} lite={quality.lite} segments={quality.segments} cloudsRef={cloudsRef}/>
              <SatelliteTiles cols={quality.tiles[0]} rows={quality.tiles[1]}/>
            </group>

            {listings.map((l, i) => {
              const { lat, lng } = pinLatLng(l);
              if (!isFinite(lat) || !isFinite(lng)) return null;
              const label = (l.title || 'Terreno').length > 26 ? l.title.slice(0, 26) + '…' : (l.title || 'Terreno');
              return (
                <LandPin key={l.listing_id || i} lat={lat} lon={lng} label={label}
                         color={l.listing_type === 'rent' ? '#8dd8ff' : '#b4ff00'}
                         pinned={selected?.listing_id === l.listing_id} labelPortal={labelPortal}
                         onClick={() => handlePin(l)}/>
              );
            })}
            {picked && <LandPin lat={picked.lat} lon={picked.lng} label="Posizione selezionata" color="#ff9b5e" pinned labelPortal={labelPortal}/>}
            {myLocation && <LandPin lat={myLocation.lat} lon={myLocation.lng} label="Tu sei qui" color="#ffffff" pinned labelPortal={labelPortal}/>}
          </group>

          <OrbitControls makeDefault enablePan={false} enableZoom enableDamping dampingFactor={0.08}
                         minDistance={MIN_DIST} maxDistance={MAX_DIST} target={[0, 0, 0]}/>

          {quality.bloom && (
            <EffectComposer disableNormalPass multisampling={0}>
              <Bloom intensity={0.5} luminanceThreshold={0.7} luminanceSmoothing={0.25} kernelSize={KernelSize.LARGE} mipmapBlur/>
              <Vignette eskil={false} offset={0.2} darkness={0.7}/>
            </EffectComposer>
          )}
        </Suspense>
      </Canvas>
      <div ref={labelPortal} className="absolute inset-0 pointer-events-none overflow-hidden z-[5]"/>

      <GlobeOverlay
        ready={ready}
        count={showHud ? listings.length : null}
        hudLabel={hudLabel}
        pickMode={!!onPick}
        selected={selected}
        onCloseSelected={() => setSelected(null)}
        onZoomIn={() => apiRef.current?.zoomBy(0.55)}
        onZoomOut={() => apiRef.current?.zoomBy(1.8)}
        onReset={() => { setSelected(null); apiRef.current?.reset(); }}
      />
    </div>
  );
}
